import * as React from "react";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from "@fluentui/react-components";
import {
  CheckmarkCircle20Filled,
  DismissCircle20Filled,
  ClockArrowDownload20Filled,
  Search20Filled,
  Subtract20Filled,
  Prohibited20Filled,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Search20Regular,
  ArrowReset20Regular,
} from "@fluentui/react-icons";
import {
  VERTRAGSKATALOG,
  STATUS_LABEL,
  MANUAL_STATUS,
  matchRiskCategoryFromRiskType,
  type StatusValue,
  type RiskCategoryKey,
} from "../data/vertragskatalog";
import { apiUrl } from "../utils/api";
import { getTaxonomy, type InsuranceClassDto } from "../services/spartenService";
import { useStickyStackOffset } from "../hooks/useStickyStackOffset";
import { REFINED } from "./risikoanalyseStep/tokens";
import { GesellschaftLogo } from "./redesign/contractRiskCells";
import { useVerbundScope } from "./verbundScope/useVerbundScope";
import { VerbundScopeBar } from "./verbundScope/VerbundScopeBar";
import { risikoobjektLabel, type RiskObjectLite } from "./contactRisks/risikoobjektLabel";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openContractFormWindow } from "./windowManager/ContractFormWindow";

// ---------- Lokale Taxonomie-Typen (aus der API gebildet) ------------------

type Art = {
  id: number; // InsuranceProduct.id
  name: string;
  detail?: string;
  riskCategories: RiskCategoryKey[];
};
type Kat = { id: number; name: string; arten: Art[] };

// ---------- Types ----------------------------------------------------------

type BackendContract = {
  id: number;
  contractNbr: string | null;
  insuranceNbr?: string | null;
  branch: string | null;
  /** 'FV' = fremdbetreut, 'EV' = eigener Bestand, null = nicht erhoben. */
  bestandsart?: "EV" | "FV" | null;
  riskLocation?: string | null;
  remark?: string | null;
  beginPolicyTerm?: string | null;
  endPolicyTerm?: string | null;
  mainDueDate?: string | null;
  premiumAnnualGross?: number | null;
  annualFee?: number | null;
  /** Alter Freitext-Versicherer (Kontakt). Nur noch Rückfall, s. `gesellschaft`. */
  insurer?: { id?: number; displayName?: string | null } | null;
  /**
   * Gesellschaft als Stammsatz (GDV-Truth) — Quelle für Logo und Bezeichnung,
   * dieselbe wie in der Vertragsliste unter /vertraege.
   */
  gesellschaft?: { id?: number; name?: string | null } | null;
  gesellschaftId?: number | null;
  contact?: { id?: number; displayName?: string | null } | null;
  insuranceProduct?: { id?: number; name?: string | null } | null;
};

type RiskCharLite = { nameBezeichnung?: string; riskCharacteristicValue?: string };
type BackendRiskObject = {
  id: number;
  name?: string | null;
  riskType?: string | null;
  characteristics?: RiskCharLite[];
  contracts?: Array<{ id: number }>;
};

type Override = {
  status?: StatusValue;
  deckungsbeschreibung?: string;
};

// ---------- riskCategories je Vertragsart (Übergang) -----------------------
// Die Gap-Erkennung (unversicherte Risikoobjekte je Vertragsart) braucht die
// Zuordnung Vertragsart → Risikokategorien. Die backend-managed Taxonomie führt
// diese (noch) nicht; bis dahin per Namens-Lookup aus dem statischen Katalog.

const normName = (s: string | null | undefined): string =>
  (s ?? "").replace(/\s+/g, " ").trim().toLowerCase();

const RISK_CAT_BY_NAME: Map<string, RiskCategoryKey[]> = (() => {
  const m = new Map<string, RiskCategoryKey[]>();
  for (const kat of VERTRAGSKATALOG) {
    for (const art of kat.arten) {
      const key = normName(art.name);
      if (!m.has(key)) m.set(key, art.riskCategories);
    }
  }
  return m;
})();

const riskCategoriesFor = (name: string): RiskCategoryKey[] =>
  RISK_CAT_BY_NAME.get(normName(name)) ?? [];

// ---------- Storage --------------------------------------------------------

const storageKey = (contactId: number) => `vertragsaufstellung:${contactId}`;
const overrideKey = (artId: number) => `p${artId}::any`;

const loadOverrides = (contactId: number): Record<string, Override> => {
  try {
    const raw = localStorage.getItem(storageKey(contactId));
    return raw ? (JSON.parse(raw) as Record<string, Override>) : {};
  } catch {
    return {};
  }
};

const saveOverridesLS = (contactId: number, data: Record<string, Override>) => {
  try {
    localStorage.setItem(storageKey(contactId), JSON.stringify(data));
  } catch {
    // ignore
  }
};

// ---------- Status visuals -------------------------------------------------

const ALL_STATUS: StatusValue[] = [
  "bestehend",
  "nicht_bestehend",
  "angebotsphase",
  "in_pruefung",
  "nicht_erforderlich",
  "nicht_gewuenscht",
];

const statusVisual = (s: StatusValue) => {
  switch (s) {
    case "bestehend":
      return { color: "#0e7c3a", bg: "#e7f6ec", icon: CheckmarkCircle20Filled };
    case "nicht_bestehend":
      return { color: "#b91c1c", bg: "#fdecec", icon: DismissCircle20Filled };
    case "angebotsphase":
      return { color: "#a16207", bg: "#fff7e0", icon: ClockArrowDownload20Filled };
    case "in_pruefung":
      return { color: "#1e40af", bg: "#e6efff", icon: Search20Filled };
    case "nicht_erforderlich":
      return { color: "#525252", bg: "#eeeeee", icon: Subtract20Filled };
    case "nicht_gewuenscht":
      return { color: "#525252", bg: "#eeeeee", icon: Prohibited20Filled };
  }
};

// ---------- Formatters ----------------------------------------------------

const formatEuro = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
};

const formatDate = (raw: string | null | undefined): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("de-DE");
};

// ---------- Spaltenbreiten -------------------------------------------------

/**
 * JEDE Spalte mit Beschriftung und Breite, in Anzeigereihenfolge.
 *
 * Eine einzige Quelle für drei Dinge: die `<col>`-Elemente, die `min-width` der
 * Tabelle und die Überschriften. Vorher waren Breiten und `min-width` getrennt
 * gepflegt und auseinandergelaufen: `min-width: 1180px` bei 1360px
 * Spaltenbreite. Bei `table-layout: fixed` verteilt der Browser fehlende Breite
 * nicht — die eine Spalte OHNE feste Angabe (die Gesellschaft) bekam schlicht
 * null Pixel, ihre Überschrift verschwand und ihr Inhalt lief über die
 * Nachbarspalte.
 *
 * Die Überschriften stehen seit dem klebenden Kopf ZWEIMAL im DOM (sichtbare
 * Kopfzeile + `<thead>` für Screenreader, s. `ListView`) — von Hand doppelt
 * gepflegt wären sie sicher irgendwann verschieden.
 */
const COLUMN_DEFS = [
  { label: "Vertragsart", width: 280 },
  { label: "Risikoobjekt", width: 240 },
  { label: "Status", width: 160 },
  { label: "Prämie", width: 110 },
  { label: "Beginn", width: 110 },
  { label: "Ablauf", width: 110 },
  { label: "Vertragsnummer", width: 150 },
  { label: "Gesellschaft", width: 230 },
  { label: "VN", width: 200 },
] as const;

const COL_W_SUM = COLUMN_DEFS.reduce((a, c) => a + c.width, 0);

/** Feine Zeilentrenner — wie im Kennzahlen-Reiter, damit die Reiter zusammenpassen. */
const COL_LINE = "#f6f1f5";

// ---------- Styles ---------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    // KEINE Höhenbegrenzung: der Reiter wächst mit seinem Inhalt, gescrollt wird
    // die SEITE. Vorher füllte er per `useFillViewportHeight` das Fenster und die
    // Tabelle scrollte in sich — bei 231px klebendem Kontaktkopf plus
    // Werkzeugleiste, Status-Filter und Ebenen-Leiste blieb dafür kaum Platz.
    // Dieselbe Lösung wie im Kennzahlen-Reiter (siehe `useStickyStackOffset`).
    minHeight: 0,
    minWidth: 0,
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.md}px`,
    color: REFINED.ink,
    backgroundColor: REFINED.white,
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    backgroundColor: REFINED.white,
    // Klebt unter dem Kontaktkopf — sonst wären Suche und Status-Filter beim
    // Scrollen durch eine lange Vertragsliste außer Sicht.
    position: "sticky",
    top: "var(--kt-head-top, 0px)",
    zIndex: 6,
  },
  topbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    minWidth: 0,
    flexWrap: "wrap",
  },
  title: {
    fontWeight: 700,
    fontSize: `${REFINED.fs.lg}px`,
    color: REFINED.ink,
    whiteSpace: "nowrap",
  },
  searchWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    ...shorthands.padding("4px", "8px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink2,
    ":focus-within": {
      ...shorthands.borderColor(REFINED.aub),
      boxShadow: `0 0 0 3px ${REFINED.aub50}`,
    },
  },
  searchInput: {
    ...shorthands.border("none"),
    outline: "none",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.sm}px`,
    color: REFINED.ink,
    width: "200px",
    backgroundColor: "transparent",
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    ...shorthands.padding("6px", "12px"),
    // 12px statt 11px und `ink2` statt Fluent-Grau: die Filterleiste ist
    // Bedienelement, nicht Fußnote.
    fontSize: `${REFINED.fs.xs}px`,
    color: REFINED.ink2,
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    flexWrap: "wrap",
    backgroundColor: REFINED.white,
  },
  legendLabel: { color: REFINED.ink2, fontWeight: 600, marginRight: "4px" },
  scopeWrap: {
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    backgroundColor: REFINED.white,
  },
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    ...shorthands.padding("4px", "9px"),
    ...shorthands.borderRadius("12px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    cursor: "pointer",
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 600,
    fontFamily: REFINED.fontUI,
    ":hover": { ...shorthands.borderColor(REFINED.aub200) },
  },
  filterChipActive: {
    ...shorthands.borderColor(REFINED.aub),
    fontWeight: 700,
  },
  body: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: "flex",
  },
  scroll: {
    flex: 1,
    minWidth: 0,
    display: "flex",
  },
  /**
   * Klammer um klebende Kopfzeile und Tabelle — sie begrenzt, wie weit der Kopf
   * klebt (`position: sticky` endet an der Unterkante des umgebenden Blocks).
   *
   * BEWUSST kein Flex-Container: in einer Flex-Spalte wäre die Haupt-Achse
   * senkrecht, und `flex: 1` an der Tabelle würde ihre Höhe aushandeln statt
   * sie vom Inhalt bestimmen zu lassen. Als normaler Block stapeln sich Kopf
   * und Tabelle einfach untereinander.
   */
  listOuter: {
    flex: 1,
    minWidth: 0,
  },
  /**
   * Die sichtbare Kopfzeile — klebt am `<main>`-Scroller unter der Werkzeugleiste.
   *
   * Sie steht BEWUSST außerhalb von `listWrap`: ein Element mit `overflow` auf
   * einer Achse wird zum Scrollport für ALLES `position: sticky` darin. Läge die
   * Kopfzeile im waagerechten Scroller, klebte sie `--kt-thead-top` Pixel
   * unterhalb DESSEN Oberkante statt unter der Werkzeugleiste — genau der
   * Fehler, der sie zuletzt mitten in die Tabelle gesetzt hat.
   *
   * `overflow: hidden` schneidet den Teil der Kopftabelle ab, der über die
   * Breite hinausragt; verschoben wird sie per `translateX` im Gleichlauf mit
   * dem Scroller darunter.
   */
  headStick: {
    position: "sticky",
    top: "var(--kt-thead-top, 0px)",
    zIndex: 3,
    ...shorthands.overflow("hidden"),
    backgroundColor: REFINED.fieldBg,
  },
  headTable: {
    // Der Gleichlauf schreibt hier `transform` — das Vorhalten spart dem Browser
    // beim ersten waagerechten Scroll das Neuanlegen der Ebene.
    willChange: "transform",
  },
  /**
   * Der waagerechte Scroll gehört HIERHER, nicht auf die Seite.
   *
   * Ohne eigene Scrollfläche schob die 1.590px breite Tabelle den ganzen
   * `<main>`-Bereich nach rechts: Kontaktkopf, Reiterleiste und Werkzeugleiste
   * wanderten mit aus dem Bild und verschwanden hinter der festen Seitenleiste.
   * Gescrollt werden soll die Tabelle, nicht die Seite.
   */
  listWrap: {
    overflowX: "auto",
  },
  list: {
    width: "100%",
    // Aus den Spaltenbreiten GERECHNET, nicht danebengeschrieben. Vorher stand
    // hier 1180px, während die Spalten 1360px beanspruchten: bei `table-layout:
    // fixed` bekam die einzige Spalte ohne feste Breite — die Gesellschaft —
    // dadurch NULL Pixel. Ihre Überschrift war unsichtbar und die Namen der
    // Versicherer liefen über die VN-Spalte darüber.
    minWidth: `${COL_W_SUM}px`,
    borderCollapse: "collapse",
    fontSize: `${REFINED.fs.sm}px`,
    tableLayout: "fixed",
  },
  listTh: {
    // Klebt NICHT selbst — das übernimmt `headStick` um die Kopftabelle herum
    // (gemessene Höhe von Kontaktkopf + Werkzeugleiste, s. `useStickyStackOffset`).
    backgroundColor: REFINED.fieldBg,
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    ...shorthands.padding("8px", "10px"),
    fontWeight: 700,
    textAlign: "left",
    fontSize: `${REFINED.fs.sm}px`,
    color: REFINED.ink2,
    whiteSpace: "nowrap",
  },
  /**
   * Der `<thead>` der Datentabelle: unsichtbar, aber im Barrierefreiheitsbaum.
   *
   * Die sichtbare Kopfzeile steht in einer eigenen Tabelle (s. `headStick`) und
   * ist dort `aria-hidden`. Ohne diesen zweiten, stummen Kopf verlöre die
   * Datentabelle die Zuordnung Spalte ↔ Zelle — ein Screenreader läse nur noch
   * Werte ohne Bezeichnung vor. `display: none` scheidet deshalb aus (nimmt die
   * Zellen aus dem Baum); stattdessen null Höhe und der Text 1px klein geclippt.
   */
  srHeadCell: {
    ...shorthands.padding("0"),
    height: 0,
    lineHeight: 0,
    fontSize: 0,
  },
  srHeadText: {
    position: "absolute",
    width: "1px",
    height: "1px",
    ...shorthands.overflow("hidden"),
    clipPath: "inset(50%)",
    whiteSpace: "nowrap",
  },
  listTd: {
    ...shorthands.borderBottom("1px", "solid", COL_LINE),
    ...shorthands.padding("7px", "10px"),
    verticalAlign: "top",
    // Werte in `ink` statt im Fluent-Sekundärgrau — dieselbe Entscheidung wie im
    // Kennzahlen-Reiter: die Zahlen sind der Inhalt, nicht die Beschriftung.
    color: REFINED.ink,
    fontVariantNumeric: "tabular-nums",
  },
  listCategoryRow: {
    backgroundColor: REFINED.aub50,
    color: REFINED.ink,
    fontWeight: 700,
    fontSize: `${REFINED.fs.md}px`,
    cursor: "pointer",
    ":hover td": { backgroundColor: REFINED.aub100 },
  },
  artNameIndent: {
    paddingLeft: "48px",
    ...shorthands.borderLeft("3px", "solid", REFINED.aub200),
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    ...shorthands.padding("3px", "8px"),
    ...shorthands.borderRadius("12px"),
    fontSize: "11px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: "pointer",
    ...shorthands.border("none"),
    fontFamily: "inherit",
  },
  manualBadge: {
    fontSize: "9px",
    color: REFINED.ink3,
    fontStyle: "italic",
    marginLeft: "4px",
  },
  riskObjList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  riskObjBtn: {
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    cursor: "pointer",
    textAlign: "left",
    ...shorthands.padding("0"),
    fontSize: "11px",
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    fontFamily: "inherit",
    ":hover": { color: REFINED.aub700 },
  },
  riskObjBtnGap: {
    color: REFINED.goldInk,
  },
  // Fremdvertrag-Kennzeichen an der Vertragsnummer. Bewusst zurückhaltend: Es
  // steht neben der Nummer jedes fremdbetreuten Vertrags und soll die
  // Aufstellung nicht in ein Ampelfeld verwandeln — beim Eigenvertrag steht
  // dort gar nichts, denn das ist der Normalfall.
  fvBadge: {
    display: "inline-block",
    marginLeft: "6px",
    ...shorthands.padding("1px", "5px"),
    ...shorthands.borderRadius("3px"),
    ...shorthands.border("1px", "solid", REFINED.aub200),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub700,
    fontSize: "9px",
    fontWeight: 700,
    letterSpacing: "0.04em",
    verticalAlign: "1px",
    whiteSpace: "nowrap",
  },
  emptyState: {
    ...shorthands.padding("24px"),
    textAlign: "center",
    color: REFINED.ink3,
    fontSize: "12px",
  },
});

// ---------- Component -----------------------------------------------------

type Props = {
  kontaktId: number;
  contactName?: string;
};

/**
 * Spaltenzahl für `colSpan` der Kategoriezeilen — aus `COLUMN_DEFS` abgeleitet,
 * damit sie nicht getrennt gepflegt wird.
 */
const COLUMNS = COLUMN_DEFS.length;

const Vertragsaufstellung: React.FC<Props> = ({ kontaktId, contactName }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const topbarRef = useRef<HTMLDivElement>(null);
  useStickyStackOffset(rootRef, topbarRef);

  const [taxonomy, setTaxonomy] = useState<InsuranceClassDto[]>([]);
  const [contracts, setContracts] = useState<BackendContract[]>([]);
  const [risikoobjekte, setRisikoobjekte] = useState<BackendRiskObject[]>([]);
  const [overrides, setOverrides] = useState<Record<string, Override>>(() =>
    kontaktId ? loadOverrides(kontaktId) : {},
  );
  const [search, setSearch] = useState("");
  const [collapsedCats, setCollapsedCats] = useState<Set<number>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<StatusValue>>(new Set());
  /**
   * Unternehmensverbund — dieselbe Bedienung wie im Kennzahlen-Reiter: Ebene „Diese
   * Firma" / „Unternehmensverbund" plus abwählbare Firmen-Chips.
   *
   * Löst den früheren VN-Filter ab, der ein anderes Verhalten hatte: er lud
   * IMMER den ganzen Verbund und blendete danach aus. Wer nur die eigene Firma
   * sehen wollte, bekam also trotzdem alle Verträge des Konzerns über die
   * Leitung, und die Voreinstellung war „alles" statt „diese Firma". Jetzt
   * bestimmt die Auswahl, was überhaupt geladen wird.
   */
  const scope = useVerbundScope(kontaktId, contactName);
  const selectedKey = scope.selectedIds.join(",");

  // Taxonomie einmalig laden.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const tax = await getTaxonomy();
        if (!aborted) setTaxonomy(tax);
      } catch (err) {
        console.error("Vertragsaufstellung: taxonomy load failed", err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  useEffect(() => {
    if (!kontaktId) return;
    let aborted = false;
    // Verträge der GEWÄHLTEN Firmen; risk-objects bleiben beim aktuellen Kontakt
    // (unversicherte Risiken dieser Firma). Die Auswahl ist nie leer — dafür
    // sorgt `useVerbundScope` —, der Rückfall auf `kontaktId` deckt nur den
    // ersten Render ab, bevor der Verbund geladen ist.
    const ids = scope.selectedIds.length > 0 ? scope.selectedIds : [kontaktId];
    (async () => {
      try {
        const [cRes, rRes] = await Promise.all([
          fetch(apiUrl(`contracts/by-contacts`, { ids: ids.join(",") })),
          fetch(apiUrl(`risk-objects/by-contact/${kontaktId}`)),
        ]);
        const cData = cRes.ok ? ((await cRes.json()) as BackendContract[]) : [];
        const rData = rRes.ok ? ((await rRes.json()) as BackendRiskObject[]) : [];
        if (aborted) return;
        setContracts(Array.isArray(cData) ? cData : []);
        setRisikoobjekte(Array.isArray(rData) ? rData : []);
      } catch (err) {
        console.error("Vertragsaufstellung: load failed", err);
      }
    })();
    return () => {
      aborted = true;
    };
    // `selectedKey` statt `scope.selectedIds`: das Array bekommt bei jedem Render
    // eine neue Identität und löste sonst eine Fetch-Schleife aus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kontaktId, selectedKey]);

  useEffect(() => {
    setOverrides(kontaktId ? loadOverrides(kontaktId) : {});
  }, [kontaktId]);

  // Taxonomie (API) → lokale Kat/Art-Struktur (mit riskCategories-Lookup).
  const catalog: Kat[] = useMemo(
    () =>
      taxonomy.map((c) => ({
        id: c.id,
        name: c.insuranceClass,
        arten: (c.products ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          detail: p.detail ?? undefined,
          riskCategories: riskCategoriesFor(p.name),
        })),
      })),
    [taxonomy],
  );

  // contract.id -> risk objects linked to it
  const risksByContract = useMemo(() => {
    const m = new Map<number, BackendRiskObject[]>();
    for (const r of risikoobjekte) {
      for (const c of r.contracts ?? []) {
        if (!c?.id) continue;
        const arr = m.get(c.id) ?? [];
        arr.push(r);
        m.set(c.id, arr);
      }
    }
    return m;
  }, [risikoobjekte]);

  // category key -> risk objects of that category
  const risksByCategory = useMemo(() => {
    const m = new Map<RiskCategoryKey, BackendRiskObject[]>();
    for (const r of risikoobjekte) {
      const cat = matchRiskCategoryFromRiskType(r.riskType ?? undefined);
      if (!cat) continue;
      const arr = m.get(cat) ?? [];
      arr.push(r);
      m.set(cat, arr);
    }
    return m;
  }, [risikoobjekte]);

  /**
   * product.id → passende Verträge (deterministisch über `insuranceProduct.id`).
   *
   * Kein VN-Filter mehr: geladen wird bereits genau die gewählte Firmenmenge.
   *
   * ACHTUNG, das ist eine harte Kante: ein Vertrag OHNE `insuranceProduct`
   * erscheint in dieser Aufstellung überhaupt nicht — sie ist nach Vertragsart
   * gegliedert, und ohne Produkt gibt es keine Zeile, unter der er stehen
   * könnte. Er ist damit nicht „unsichtbar wegen eines Filters", sondern
   * strukturell nicht darstellbar.
   */
  const contractsByArt = useMemo(() => {
    const m = new Map<number, BackendContract[]>();
    for (const c of contracts) {
      const pid = c.insuranceProduct?.id;
      if (!pid) continue;
      const arr = m.get(pid) ?? [];
      arr.push(c);
      m.set(pid, arr);
    }
    return m;
  }, [contracts]);

  /**
   * Verträge, die es mangels Vertragsart NICHT in die Tabelle schaffen. Wird als
   * Hinweis angezeigt: eine Aufstellung, die stillschweigend Verträge unterschlägt,
   * liest sich wie „es gibt keine".
   */
  const ohneVertragsart = useMemo(
    () => contracts.filter((c) => !c.insuranceProduct?.id).length,
    [contracts],
  );

  const getStatusForArt = useCallback(
    (art: Art): { status: StatusValue; auto: boolean } => {
      const ov = overrides[overrideKey(art.id)];
      if (ov?.status) return { status: ov.status, auto: false };
      const matching = contractsByArt.get(art.id) ?? [];
      return { status: matching.length > 0 ? "bestehend" : "nicht_bestehend", auto: true };
    },
    [overrides, contractsByArt],
  );

  // Such- + Statusfilter auf den Katalog anwenden.
  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog
      .map((kat) => ({
        ...kat,
        arten: kat.arten.filter((art) => {
          if (q) {
            const hay = `${art.name} ${art.detail ?? ""} ${kat.name}`.toLowerCase();
            if (!hay.includes(q)) return false;
          }
          if (statusFilter.size > 0) {
            if (!statusFilter.has(getStatusForArt(art).status)) return false;
          }
          return true;
        }),
      }))
      .filter((k) => k.arten.length > 0);
  }, [catalog, search, statusFilter, getStatusForArt]);

  const setOverride = useCallback(
    (artId: number, patch: Partial<Override>) => {
      setOverrides((prev) => {
        const k = overrideKey(artId);
        const next = { ...prev };
        const merged: Override = { ...next[k], ...patch };
        if (!merged.status && !merged.deckungsbeschreibung) {
          delete next[k];
        } else {
          next[k] = merged;
        }
        if (kontaktId) saveOverridesLS(kontaktId, next);
        return next;
      });
    },
    [kontaktId],
  );

  const clearStatusOverride = useCallback(
    (artId: number) => {
      setOverrides((prev) => {
        const k = overrideKey(artId);
        if (!prev[k]) return prev;
        const next = { ...prev };
        const remaining: Override = { ...next[k] };
        delete remaining.status;
        if (!remaining.deckungsbeschreibung) {
          delete next[k];
        } else {
          next[k] = remaining;
        }
        if (kontaktId) saveOverridesLS(kontaktId, next);
        return next;
      });
    },
    [kontaktId],
  );

  const toggleCategory = useCallback((catId: number) => {
    setCollapsedCats((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }, []);

  const toggleStatusFilter = useCallback((s: StatusValue) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  const { openWindow } = useWindowManager();

  const navigateToRiskContract = useCallback(
    (risk: BackendRiskObject) => {
      const t = (risk.riskType ?? "").toLowerCase();
      if (t.includes("kfz") || t.includes("immo")) {
        const variant: "kfz" | "immobilie" = t.includes("kfz") ? "kfz" : "immobilie";
        openContractFormWindow(openWindow, {
          variant,
          contactId: kontaktId ?? null,
          contactName: contactName ?? null,
          riskObjectId: risk.id,
        });
        return;
      }
      navigate("/vertrag/new", {
        state: { kontaktId, riskObjectId: risk.id },
      });
    },
    [navigate, kontaktId, contactName, openWindow],
  );

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.topbar} ref={topbarRef}>
        <div className={styles.topbarLeft}>
          <span className={styles.title}>
            Vertragsaufstellung{contactName ? ` · ${contactName}` : ""}
          </span>
          <label className={styles.searchWrap}>
            <Search20Regular />
            <input
              className={styles.searchInput}
              placeholder="Suche Vertragsart…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className={styles.legend}>
        <span className={styles.legendLabel}>Status filtern:</span>
        {ALL_STATUS.map((s) => {
          const v = statusVisual(s);
          const Icon = v.icon;
          const active = statusFilter.has(s);
          return (
            <button
              key={s}
              type="button"
              className={mergeClasses(styles.filterChip, active && styles.filterChipActive)}
              style={active ? { backgroundColor: v.bg, color: v.color } : undefined}
              onClick={() => toggleStatusFilter(s)}
              title={`Nach Status „${STATUS_LABEL[s]}“ filtern`}
            >
              <Icon style={{ color: v.color, fontSize: 14 }} /> {STATUS_LABEL[s]}
            </button>
          );
        })}
        {statusFilter.size > 0 ? (
          <button
            type="button"
            className={styles.filterChip}
            onClick={() => setStatusFilter(new Set())}
            title="Filter zurücksetzen"
          >
            <ArrowReset20Regular style={{ fontSize: 14 }} /> Alle
          </button>
        ) : null}
      </div>

      {/* Firmenauswahl — dieselbe Leiste und dieselbe Bedienung wie im Kennzahlen-Reiter. */}
      <div className={styles.scopeWrap}>
        <VerbundScopeBar
          scope={scope}
          hint={
            scope.isConsolidated
              ? `Verträge aus ${scope.selectedIds.length} Firmen — die Spalte „VN" nennt je Zeile den Versicherungsnehmer.`
              : undefined
          }
        />
      </div>

      {ohneVertragsart > 0 && (
        // Ohne diesen Hinweis liest sich eine leere Aufstellung als „keine
        // Verträge vorhanden", obwohl es welche gibt — sie hängen nur an keiner
        // Vertragsart und haben deshalb keine Zeile.
        <div className={styles.legend}>
          <span className={styles.legendLabel}>Hinweis:</span>
          {ohneVertragsart === 1
            ? "1 Vertrag ist keiner Vertragsart zugeordnet und erscheint deshalb unten nicht."
            : `${ohneVertragsart} Verträge sind keiner Vertragsart zugeordnet und erscheinen deshalb unten nicht.`}
        </div>
      )}

      <div className={styles.body}>
        <div className={styles.scroll}>
          <ListView
            styles={styles}
            filteredCatalog={filteredCatalog}
            contractsByArt={contractsByArt}
            risksByContract={risksByContract}
            risksByCategory={risksByCategory}
            getStatusForArt={getStatusForArt}
            collapsedCats={collapsedCats}
            toggleCategory={toggleCategory}
            onStatusChange={(artId, s) => setOverride(artId, { status: s })}
            onResetStatus={clearStatusOverride}
            onRiskClick={navigateToRiskContract}
          />
        </div>
      </div>
    </div>
  );
};

// ---------- List View -----------------------------------------------------

type ListViewProps = {
  styles: ReturnType<typeof useStyles>;
  filteredCatalog: Kat[];
  contractsByArt: Map<number, BackendContract[]>;
  risksByContract: Map<number, BackendRiskObject[]>;
  risksByCategory: Map<RiskCategoryKey, BackendRiskObject[]>;
  getStatusForArt: (art: Art) => { status: StatusValue; auto: boolean };
  collapsedCats: Set<number>;
  toggleCategory: (id: number) => void;
  onStatusChange: (artId: number, s: StatusValue) => void;
  onResetStatus: (artId: number) => void;
  onRiskClick: (risk: BackendRiskObject) => void;
};

const ListView: React.FC<ListViewProps> = ({
  styles,
  filteredCatalog,
  contractsByArt,
  risksByContract,
  risksByCategory,
  getStatusForArt,
  collapsedCats,
  toggleCategory,
  onStatusChange,
  onResetStatus,
  onRiskClick,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const headTableRef = useRef<HTMLTableElement>(null);

  /**
   * Hält die klebende Kopfzeile waagerecht auf Höhe der Tabelle.
   *
   * Die Kopfzeile liegt außerhalb des Scrollers (sonst klebte sie an dessen
   * Oberkante statt unter der Werkzeugleiste), bewegt sich also beim
   * Rechtsscrollen nicht von selbst mit. Geschrieben wird direkt ins DOM statt
   * über State: bei jedem Scroll-Frame zu rendern würde eine Tabelle mit
   * hunderten Zeilen sichtbar ruckeln lassen.
   */
  const syncHead = useCallback(() => {
    const head = headTableRef.current;
    if (head) head.style.transform = `translateX(${-(wrapRef.current?.scrollLeft ?? 0)}px)`;
  }, []);

  // Nach jedem Render nachziehen: Filter- oder Firmenwechsel tauschen den Inhalt
  // aus, während `scrollLeft` stehen bleibt.
  useEffect(syncHead);

  if (filteredCatalog.length === 0) {
    return <div className={styles.emptyState}>Keine Treffer.</div>;
  }
  const cols = (
    <colgroup>
      {COLUMN_DEFS.map((c) => (
        <col key={c.label} style={{ width: c.width }} />
      ))}
    </colgroup>
  );
  return (
    <div className={styles.listOuter}>
      {/* Sichtbare Kopfzeile — eigene Tabelle, damit sie am Seiten-Scroller
          kleben kann; für Screenreader stumm, die lesen den `<thead>` unten. */}
      <div className={styles.headStick} aria-hidden="true">
        <table
          ref={headTableRef}
          className={mergeClasses(styles.list, styles.headTable)}
        >
          {cols}
          <thead>
            <tr>
              {COLUMN_DEFS.map((c) => (
                <th key={c.label} className={styles.listTh}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>
      <div className={styles.listWrap} ref={wrapRef} onScroll={syncHead}>
        <table className={styles.list}>
          {cols}
          <thead>
            <tr>
              {COLUMN_DEFS.map((c) => (
                <th key={c.label} scope="col" className={styles.srHeadCell}>
                  <span className={styles.srHeadText}>{c.label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredCatalog.map((kat) => {
              const collapsed = collapsedCats.has(kat.id);
              return (
                <React.Fragment key={kat.id}>
                  <tr
                    className={styles.listCategoryRow}
                    onClick={() => toggleCategory(kat.id)}
                  >
                    <td className={styles.listTd} colSpan={COLUMNS}>
                      {collapsed ? <ChevronRight20Regular /> : <ChevronDown20Regular />}{" "}
                      {kat.name}
                      <span
                        style={{
                          color: REFINED.ink3,
                          fontWeight: 400,
                          marginLeft: 8,
                        }}
                      >
                        ({kat.arten.length})
                      </span>
                    </td>
                  </tr>
                  {!collapsed &&
                    kat.arten.flatMap((art) => {
                      const matching = contractsByArt.get(art.id) ?? [];
                      const { status, auto } = getStatusForArt(art);

                      const coveredIds = new Set<number>();
                      for (const c of matching) {
                        for (const r of risksByContract.get(c.id) ?? []) {
                          coveredIds.add(r.id);
                        }
                      }
                      const applicable = new Map<number, BackendRiskObject>();
                      for (const catKey of art.riskCategories) {
                        for (const r of risksByCategory.get(catKey) ?? []) {
                          applicable.set(r.id, r);
                        }
                      }
                      const uncoveredRisks = Array.from(applicable.values()).filter(
                        (r) => !coveredIds.has(r.id),
                      );

                      const rows: React.ReactNode[] = [];

                      // 1) one row per matching contract
                      matching.forEach((contract, idx) => {
                        const risks = risksByContract.get(contract.id) ?? [];
                        // Gesellschaft (GDV-Stammsatz) ist die Quelle für Logo und
                        // Name — dieselbe wie in der Vertragsliste unter /vertraege.
                        // Der alte Freitext-Versicherer bleibt als Rückfall, solange
                        // nicht jeder Vertrag zugeordnet ist (auf PROD sind es
                        // Tausende); ohne ihn stünde dort sonst nur ein Strich.
                        const gesellschaftId = contract.gesellschaftId ?? contract.gesellschaft?.id;
                        const gesellschaftName =
                          contract.gesellschaft?.name ?? contract.insurer?.displayName ?? null;
                        const premium =
                          contract.premiumAnnualGross ?? contract.annualFee ?? null;
                        rows.push(
                          <tr key={`${art.id}::c::${contract.id}`}>
                            <td className={mergeClasses(styles.listTd, styles.artNameIndent)}>
                              {idx === 0 ? (
                                <>
                                  <div>{art.name}</div>
                                  {art.detail && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: REFINED.ink3,
                                      }}
                                    >
                                      {art.detail}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: REFINED.ink3 }}>
                                  ↳
                                </span>
                              )}
                            </td>
                            <td className={styles.listTd}>
                              {risks.length === 0 ? (
                                <span style={{ color: REFINED.ink3 }}>
                                  —
                                </span>
                              ) : (
                                <div className={styles.riskObjList}>
                                  {risks.map((r) => (
                                    <button
                                      key={r.id}
                                      type="button"
                                      className={styles.riskObjBtn}
                                      onClick={() => onRiskClick(r)}
                                      title="Risikoobjekt öffnen / Vertrag bearbeiten"
                                    >
                                      {risikoobjektLabel(r as RiskObjectLite)}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className={styles.listTd}>
                              <StatusMenu
                                styles={styles}
                                status={status}
                                auto={auto}
                                onChange={(s) => onStatusChange(art.id, s)}
                                onReset={() => onResetStatus(art.id)}
                              />
                            </td>
                            <td className={styles.listTd}>{formatEuro(premium)}</td>
                            <td className={styles.listTd}>
                              {formatDate(contract.beginPolicyTerm)}
                            </td>
                            <td className={styles.listTd}>
                              {formatDate(contract.endPolicyTerm)}
                            </td>
                            <td className={styles.listTd}>
                              {contract.contractNbr || (
                                <span style={{ color: REFINED.ink3 }}>
                                  —
                                </span>
                              )}
                              {contract.bestandsart === "FV" && (
                                <span
                                  className={styles.fvBadge}
                                  title="Fremdvertrag — läuft beim Kunden, betreut ihn aber ein anderer Makler"
                                >
                                  FV
                                </span>
                              )}
                            </td>
                            <td className={styles.listTd}>
                              <GesellschaftLogo id={gesellschaftId} name={gesellschaftName} />
                            </td>
                            <td className={styles.listTd}>
                              {contract.contact?.displayName ?? (
                                <span style={{ color: REFINED.ink3 }}>—</span>
                              )}
                            </td>
                          </tr>,
                        );
                      });

                      // 2) one row per uncovered risk object (gap)
                      uncoveredRisks.forEach((risk, idx) => {
                        const isFirstInArt = rows.length === 0 && idx === 0;
                        rows.push(
                          <tr key={`${art.id}::u::${risk.id}`}>
                            <td className={mergeClasses(styles.listTd, styles.artNameIndent)}>
                              {isFirstInArt ? (
                                <>
                                  <div>{art.name}</div>
                                  {art.detail && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: REFINED.ink3,
                                      }}
                                    >
                                      {art.detail}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: REFINED.ink3 }}>
                                  ↳
                                </span>
                              )}
                            </td>
                            <td className={styles.listTd}>
                              <button
                                type="button"
                                className={mergeClasses(styles.riskObjBtn, styles.riskObjBtnGap)}
                                onClick={() => onRiskClick(risk)}
                                title="Vertrag für dieses Risikoobjekt anlegen"
                              >
                                {risikoobjektLabel(risk as RiskObjectLite)} · Vertrag anlegen
                              </button>
                            </td>
                            <td className={styles.listTd}>
                              <StatusMenu
                                styles={styles}
                                status={status}
                                auto={auto}
                                onChange={(s) => onStatusChange(art.id, s)}
                                onReset={() => onResetStatus(art.id)}
                              />
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                          </tr>,
                        );
                      });

                      // 3) empty placeholder if nothing rendered yet
                      if (rows.length === 0) {
                        rows.push(
                          <tr key={`${art.id}::empty`}>
                            <td className={mergeClasses(styles.listTd, styles.artNameIndent)}>
                              <div>{art.name}</div>
                              {art.detail && (
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: REFINED.ink3,
                                  }}
                                >
                                  {art.detail}
                                </div>
                              )}
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <StatusMenu
                                styles={styles}
                                status={status}
                                auto={auto}
                                onChange={(s) => onStatusChange(art.id, s)}
                                onReset={() => onResetStatus(art.id)}
                              />
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                            <td className={styles.listTd}>
                              <span style={{ color: REFINED.ink3 }}>—</span>
                            </td>
                          </tr>,
                        );
                      }

                      return rows;
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Status chip with menu ----------------------------------------

const StatusMenu: React.FC<{
  styles: ReturnType<typeof useStyles>;
  status: StatusValue;
  auto: boolean;
  onChange: (s: StatusValue) => void;
  onReset: () => void;
}> = ({ styles, status, auto, onChange, onReset }) => {
  const v = statusVisual(status);
  const Icon = v.icon;

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.statusChip}
          style={{ backgroundColor: v.bg, color: v.color }}
          title="Status ändern"
        >
          <Icon style={{ fontSize: 14 }} /> {STATUS_LABEL[status]}
          {!auto && <span className={styles.manualBadge}>· manuell</span>}
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {ALL_STATUS.map((s) => {
            const sv = statusVisual(s);
            const Si = sv.icon;
            const isManual = MANUAL_STATUS.includes(s);
            return (
              <MenuItem
                key={s}
                disabled={!isManual}
                onClick={() => isManual && onChange(s)}
                icon={<Si style={{ color: sv.color, fontSize: 16 }} />}
              >
                {STATUS_LABEL[s]}
                {!isManual && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 10,
                      color: REFINED.ink3,
                    }}
                  >
                    (auto)
                  </span>
                )}
              </MenuItem>
            );
          })}
          {!auto && (
            <MenuItem onClick={onReset} icon={<ArrowReset20Regular />}>
              Auf automatisch zurücksetzen
            </MenuItem>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default Vertragsaufstellung;
